import React, { forwardRef, useContext } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import context, { sendStyles } from '@asphalt-react/context';
import { Qualifier } from '@asphalt-react/qualifier';
import { hashUtil } from '@asphalt-react/helper';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Textfield__4afgA {\n  /* default custom properties */\n  --textfield-background-color: var(\n    --interactive-fill-default,\n    hsla(0, 0%, 100%, 1)\n  );\n  --textfield-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --textfield-placeholder-color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  --textfield-border-radius: var(--roundness-1, 2px);\n  --textfield-border-color: var(\n    --interactive-border-solid,\n    solid hsla(220, 22%, 76%, 1)\n  );\n  --textfield-border-color-focus: var(\n    --interactive-focus,\n    hsla(219, 42%, 18%, 1)\n  );\n  --textfield-border-color-active: var(\n    --interactive-focus,\n    hsla(219, 42%, 18%, 1)\n  );\n  --textfield-border-color-error: var(\n    --interactive-border-support,\n    hsla(12, 92%, 36%, 1)\n  );\n  --textfield-background-color-disabled: var(\n    --interactive-disabled,\n    hsla(0, 0%, 86%, 1)\n  );\n  --textfield-background-color-hover: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n  --textfield-font-color-disabled: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --textfield-font-size-l: var(--body-l, 1.13rem);\n  --textfield-font-size-m: var(--body-m, 1rem);\n  --textfield-font-size-s: var(--body-s, 0.88rem);\n  --textfield-extent-low-height: 64px;\n  --textfield-extent-mid-height: 80px;\n  --textfield-extent-high-height: 96px;\n\n  background-color: var(--textfield-background-color);\n  border: var(--border-width, 1px) var(--textfield-border-color);\n  border-radius: var(--textfield-border-radius);\n  line-height: initial;\n  color: var(--textfield-font-color);\n  font-weight: 300;\n  font-size: var(--textfield-font-size-m);\n  box-sizing: border-box;\n  width: 320px;\n}\n\n.qualifierContainer__u82Fs {\n  --textfield-icon-focus-color: var(\n    --interactive-03-active,\n    hsla(220, 17%, 93%, 1)\n  );\n}\n\n.search__mbRSt {\n  -webkit-appearance: none;\n  /* search icon in Searchfield, overlaps the placeholder in Safari.  \n  searchfield appears rounded in iOS Safari */\n}\n\n.stretch__c8BP3 {\n  width: 100%;\n}\n.Textfield__4afgA:focus {\n  border-color: var(--textfield-border-color-focus);\n  outline: 0 none;\n}\n.Textfield__4afgA:hover {\n  background-color: var(--textfield-background-color-hover);\n}\n.Textfield__4afgA:active {\n  border-color: var(--textfield-border-color-active);\n}\n.Textfield__4afgA::-moz-placeholder {\n  color: var(--textfield-placeholder-color);\n}\n.Textfield__4afgA::placeholder {\n  color: var(--textfield-placeholder-color);\n}\n\n.invalid__2MNrD {\n  border-color: var(--textfield-border-color-error);\n  /* firefox gives default box-shadow for invalid inputs */\n  box-shadow: none;\n}\n\n.Textfield__4afgA:disabled {\n  border-color: transparent;\n  background-color: var(--textfield-background-color-disabled);\n  color: var(--textfield-font-color-disabled);\n  -webkit-text-fill-color: var(--textfield-font-color-disabled);\n  /* override Safari disabled input font color */\n  cursor: not-allowed;\n}\n.l__mZ0Pd {\n  font-size: var(--textfield-font-size-l);\n  min-height: 48px;\n  padding: 0.375rem 0.75rem;\n}\n.m__NGhsp {\n  font-size: var(--textfield-font-size-m);\n  min-height: 40px;\n  padding: 0.28rem 0.75rem;\n}\n.s__vSATO {\n  font-size: var(--textfield-font-size-s);\n  min-height: 32px;\n  padding: 0.187rem 0.75rem;\n}\n.multiline__W-nCZ {\n  resize: vertical;\n  padding: 0.75rem;\n}\n.multiline__W-nCZ.l__mZ0Pd {\n  font-size: var(--textfield-font-size-l);\n}\n\n.multiline__W-nCZ.m__NGhsp {\n  font-size: var(--textfield-font-size-m);\n}\n\n.multiline__W-nCZ.s__vSATO {\n  font-size: var(--textfield-font-size-s);\n}\n\n.multiline__W-nCZ.extentLow__c7b3W {\n  height: var(--textfield-extent-low-height);\n}\n\n.multiline__W-nCZ.extentMid__1JQUT {\n  height: var(--textfield-extent-mid-height);\n}\n\n.multiline__W-nCZ.extentHigh__0pOoO {\n  height: var(--textfield-extent-high-height);\n}\n\n.qualifierContainer__u82Fs {\n  position: relative;\n  display: inline-flex;\n  align-items: center;\n}\n\n.start__rCipR > .Textfield__4afgA {\n  padding-left: 2.5em;\n}\n\n.end__iTxWI > .Textfield__4afgA {\n  padding-right: 2.5em;\n}\n\n.start__rCipR.textContainer__IMUSl > .Textfield__4afgA {\n  padding-left: 3.2em;\n}\n\n.end__iTxWI.textContainer__IMUSl > .Textfield__4afgA {\n  padding-right: 3.2em;\n}\n\n.qualifierContainer__u82Fs > .qualifier__E-813 {\n  position: absolute;\n  display: flex;\n  align-items: center;\n  padding: 0.25em;\n}\n\n.qualifierContainer__u82Fs > .Textfield__4afgA.s__vSATO + .qualifier__E-813 {\n  padding: 0.125em;\n}\n\n.start__rCipR > .qualifier__E-813 {\n  left: 0.75em;\n}\n\n.end__iTxWI > .qualifier__E-813 {\n  right: 0.75em;\n}\n\n.pluginQualifier__LzL-9 {\n  right: 0.5em;\n  padding: 0;\n}\n\n.datefield__yDcJ2 {\n  -webkit-appearance: none;\n}\n\n.pluginEnd__RYDnX {\n  padding-right: 2.5em;\n}\n\n.textQualifier__eTPch {\n  color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n}\n\n.textQualifier__eTPch.l__mZ0Pd {\n  font-size: var(--textfield-font-size-l);\n}\n\n.textQualifier__eTPch.m__NGhsp {\n  font-size: var(--textfield-font-size-m);\n}\n\n.textQualifier__eTPch.s__vSATO {\n  font-size: var(--body-s, 0.88rem);\n}\n\n/* handle [dir=\"rtl\"] styles */\n*[dir=\"rtl\"] .start__rCipR > .Textfield__4afgA {\n  padding-right: 2.5em;\n}\n\n*[dir=\"rtl\"] .start__rCipR > .l__mZ0Pd,\n*[dir=\"rtl\"] .start__rCipR > .m__NGhsp,\n*[dir=\"rtl\"] .start__rCipR > .s__vSATO {\n  padding-left: 0.75em;\n}\n\n*[dir=\"rtl\"] .start__rCipR > .qualifier__E-813 {\n  right: 0.75em;\n  left: unset;\n}\n\n*[dir=\"rtl\"] .end__iTxWI > .Textfield__4afgA {\n  padding-left: 2.5em;\n}\n\n*[dir=\"rtl\"] .end__iTxWI > .l__mZ0Pd,\n*[dir=\"rtl\"] .end__iTxWI > .m__NGhsp,\n*[dir=\"rtl\"] .end__iTxWI > .s__vSATO {\n  padding-right: 0.75em;\n}\n\n*[dir=\"rtl\"] .end__iTxWI > .qualifier__E-813 {\n  left: 0.75em;\n  right: unset;\n}\n";
var styles = {"Textfield":"Textfield__4afgA","qualifierContainer":"qualifierContainer__u82Fs","search":"search__mbRSt","stretch":"stretch__c8BP3","invalid":"invalid__2MNrD","l":"l__mZ0Pd","m":"m__NGhsp","s":"s__vSATO","multiline":"multiline__W-nCZ","extentLow":"extentLow__c7b3W","extentMid":"extentMid__1JQUT","extentHigh":"extentHigh__0pOoO","start":"start__rCipR","end":"end__iTxWI","textContainer":"textContainer__IMUSl","qualifier":"qualifier__E-813","pluginQualifier":"pluginQualifier__LzL-9","datefield":"datefield__yDcJ2","pluginEnd":"pluginEnd__RYDnX","textQualifier":"textQualifier__eTPch"};
var stylesheet=".Textfield__4afgA {\n  /* default custom properties */\n  --textfield-background-color: var(\n    --interactive-fill-default,\n    hsla(0, 0%, 100%, 1)\n  );\n  --textfield-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --textfield-placeholder-color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  --textfield-border-radius: var(--roundness-1, 2px);\n  --textfield-border-color: var(\n    --interactive-border-solid,\n    solid hsla(220, 22%, 76%, 1)\n  );\n  --textfield-border-color-focus: var(\n    --interactive-focus,\n    hsla(219, 42%, 18%, 1)\n  );\n  --textfield-border-color-active: var(\n    --interactive-focus,\n    hsla(219, 42%, 18%, 1)\n  );\n  --textfield-border-color-error: var(\n    --interactive-border-support,\n    hsla(12, 92%, 36%, 1)\n  );\n  --textfield-background-color-disabled: var(\n    --interactive-disabled,\n    hsla(0, 0%, 86%, 1)\n  );\n  --textfield-background-color-hover: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n  --textfield-font-color-disabled: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --textfield-font-size-l: var(--body-l, 1.13rem);\n  --textfield-font-size-m: var(--body-m, 1rem);\n  --textfield-font-size-s: var(--body-s, 0.88rem);\n  --textfield-extent-low-height: 64px;\n  --textfield-extent-mid-height: 80px;\n  --textfield-extent-high-height: 96px;\n\n  background-color: var(--textfield-background-color);\n  border: var(--border-width, 1px) var(--textfield-border-color);\n  border-radius: var(--textfield-border-radius);\n  line-height: initial;\n  color: var(--textfield-font-color);\n  font-weight: 300;\n  font-size: var(--textfield-font-size-m);\n  box-sizing: border-box;\n  width: 320px;\n}\n\n.qualifierContainer__u82Fs {\n  --textfield-icon-focus-color: var(\n    --interactive-03-active,\n    hsla(220, 17%, 93%, 1)\n  );\n}\n\n.search__mbRSt {\n  -webkit-appearance: none;\n  /* search icon in Searchfield, overlaps the placeholder in Safari.  \n  searchfield appears rounded in iOS Safari */\n}\n\n.stretch__c8BP3 {\n  width: 100%;\n}\n.Textfield__4afgA:focus {\n  border-color: var(--textfield-border-color-focus);\n  outline: 0 none;\n}\n.Textfield__4afgA:hover {\n  background-color: var(--textfield-background-color-hover);\n}\n.Textfield__4afgA:active {\n  border-color: var(--textfield-border-color-active);\n}\n.Textfield__4afgA::-moz-placeholder {\n  color: var(--textfield-placeholder-color);\n}\n.Textfield__4afgA::placeholder {\n  color: var(--textfield-placeholder-color);\n}\n\n.invalid__2MNrD {\n  border-color: var(--textfield-border-color-error);\n  /* firefox gives default box-shadow for invalid inputs */\n  box-shadow: none;\n}\n\n.Textfield__4afgA:disabled {\n  border-color: transparent;\n  background-color: var(--textfield-background-color-disabled);\n  color: var(--textfield-font-color-disabled);\n  -webkit-text-fill-color: var(--textfield-font-color-disabled);\n  /* override Safari disabled input font color */\n  cursor: not-allowed;\n}\n.l__mZ0Pd {\n  font-size: var(--textfield-font-size-l);\n  min-height: 48px;\n  padding: 0.375rem 0.75rem;\n}\n.m__NGhsp {\n  font-size: var(--textfield-font-size-m);\n  min-height: 40px;\n  padding: 0.28rem 0.75rem;\n}\n.s__vSATO {\n  font-size: var(--textfield-font-size-s);\n  min-height: 32px;\n  padding: 0.187rem 0.75rem;\n}\n.multiline__W-nCZ {\n  resize: vertical;\n  padding: 0.75rem;\n}\n.multiline__W-nCZ.l__mZ0Pd {\n  font-size: var(--textfield-font-size-l);\n}\n\n.multiline__W-nCZ.m__NGhsp {\n  font-size: var(--textfield-font-size-m);\n}\n\n.multiline__W-nCZ.s__vSATO {\n  font-size: var(--textfield-font-size-s);\n}\n\n.multiline__W-nCZ.extentLow__c7b3W {\n  height: var(--textfield-extent-low-height);\n}\n\n.multiline__W-nCZ.extentMid__1JQUT {\n  height: var(--textfield-extent-mid-height);\n}\n\n.multiline__W-nCZ.extentHigh__0pOoO {\n  height: var(--textfield-extent-high-height);\n}\n\n.qualifierContainer__u82Fs {\n  position: relative;\n  display: inline-flex;\n  align-items: center;\n}\n\n.start__rCipR > .Textfield__4afgA {\n  padding-left: 2.5em;\n}\n\n.end__iTxWI > .Textfield__4afgA {\n  padding-right: 2.5em;\n}\n\n.start__rCipR.textContainer__IMUSl > .Textfield__4afgA {\n  padding-left: 3.2em;\n}\n\n.end__iTxWI.textContainer__IMUSl > .Textfield__4afgA {\n  padding-right: 3.2em;\n}\n\n.qualifierContainer__u82Fs > .qualifier__E-813 {\n  position: absolute;\n  display: flex;\n  align-items: center;\n  padding: 0.25em;\n}\n\n.qualifierContainer__u82Fs > .Textfield__4afgA.s__vSATO + .qualifier__E-813 {\n  padding: 0.125em;\n}\n\n.start__rCipR > .qualifier__E-813 {\n  left: 0.75em;\n}\n\n.end__iTxWI > .qualifier__E-813 {\n  right: 0.75em;\n}\n\n.pluginQualifier__LzL-9 {\n  right: 0.5em;\n  padding: 0;\n}\n\n.datefield__yDcJ2 {\n  -webkit-appearance: none;\n}\n\n.pluginEnd__RYDnX {\n  padding-right: 2.5em;\n}\n\n.textQualifier__eTPch {\n  color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n}\n\n.textQualifier__eTPch.l__mZ0Pd {\n  font-size: var(--textfield-font-size-l);\n}\n\n.textQualifier__eTPch.m__NGhsp {\n  font-size: var(--textfield-font-size-m);\n}\n\n.textQualifier__eTPch.s__vSATO {\n  font-size: var(--body-s, 0.88rem);\n}\n\n/* handle [dir=\"rtl\"] styles */\n*[dir=\"rtl\"] .start__rCipR > .Textfield__4afgA {\n  padding-right: 2.5em;\n}\n\n*[dir=\"rtl\"] .start__rCipR > .l__mZ0Pd,\n*[dir=\"rtl\"] .start__rCipR > .m__NGhsp,\n*[dir=\"rtl\"] .start__rCipR > .s__vSATO {\n  padding-left: 0.75em;\n}\n\n*[dir=\"rtl\"] .start__rCipR > .qualifier__E-813 {\n  right: 0.75em;\n  left: unset;\n}\n\n*[dir=\"rtl\"] .end__iTxWI > .Textfield__4afgA {\n  padding-left: 2.5em;\n}\n\n*[dir=\"rtl\"] .end__iTxWI > .l__mZ0Pd,\n*[dir=\"rtl\"] .end__iTxWI > .m__NGhsp,\n*[dir=\"rtl\"] .end__iTxWI > .s__vSATO {\n  padding-right: 0.75em;\n}\n\n*[dir=\"rtl\"] .end__iTxWI > .qualifier__E-813 {\n  left: 0.75em;\n  right: unset;\n}\n";
styleInject(css_248z);

const svgSizes = {
  sm: {
    height: "14",
    width: "14"
  },
  md: {
    height: "16",
    width: "16"
  },
  lg: {
    height: "20",
    width: "20"
  }
};

/**
 * Returns the style based on `size` value
 *
 * @param {string} size
 * @returns {Object}
 */

const sizeClass = size => {
  switch (size) {
    case "l":
      return styles.l;

    case "m":
      return styles.m;

    case "s":
      return styles.s;

    default:
      return styles.m;
  }
};
/**
 * Returns the style based on `extent` value
 *
 * @param {string} extent
 * @returns {Object}
 */

const extentClass = extent => {
  switch (extent) {
    case "low":
      return styles.extentLow;

    case "mid":
      return styles.extentMid;

    case "high":
      return styles.extentHigh;

    default:
      return styles.extentLow;
  }
};
/**
 * Create css classes from props
 *
 * @param {object} param custom props object
 * @returns {string}
 */


const createFieldClasses = ({
  size,
  stretch,
  extent,
  invalid
}) => cn(styles.Textfield, sizeClass(size), {
  [styles.stretch]: stretch
}, extentClass(extent), {
  [styles.invalid]: invalid
});
const svgSize = size => {
  switch (size) {
    case "s":
      return svgSizes.sm;

    case "m":
      return svgSizes.md;

    case "l":
      return svgSizes.lg;

    default:
      return svgSizes.md;
  }
};
/**
 * Returns filtered props
 *
 * @param {Object} props props to be filtered
 * @param {Object} options props are filtered based on options
 * @returns {Object}
 */

const filterProps = (props, {
  multiline
}) => {
  const {
    style,
    className,
    ...rest
  } = props; // Not allowing custom styles to pass to component

  if (multiline) {
    const {
      rows,
      cols,
      ...multilineProps
    } = rest;
    return multilineProps;
  }

  return rest;
};
/**
 * Checks if a value is of type string
 *
 * @param {*} value
 * @returns {bool}
 */

const isString = value => typeof value === "string";

const QualifierWrap = ({
  children,
  size,
  ...props
}) => {
  const textQualifier = isString(children);
  return /*#__PURE__*/React.createElement("span", {
    className: cn(styles.qualifier, {
      [styles.textQualifier]: textQualifier,
      [sizeClass(size)]: textQualifier
    })
  }, /*#__PURE__*/React.createElement(Qualifier, _extends({}, props, {
    iconSize: size
  }), children));
};

const Textfield = /*#__PURE__*/forwardRef(({
  size,
  multiline,
  stretch,
  qualifier,
  qualifierStart,
  qualifierEnd,
  extent,
  invalid,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet);
  const propsObject = multiline ? {
    size,
    stretch,
    extent
  } : {
    size,
    stretch
  };
  const textfieldClasses = cn({
    [styles.multiline]: multiline
  }, {
    [styles.invalid]: invalid
  }, createFieldClasses(propsObject));
  const fieldProps = filterProps(props, {
    multiline
  });

  const input = () => {
    return /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: "text",
      className: textfieldClasses,
      "aria-invalid": invalid
    }));
  };

  return multiline ? /*#__PURE__*/React.createElement("textarea", _extends({}, fieldProps, {
    ref: ref,
    className: textfieldClasses
  })) : qualifier ? /*#__PURE__*/React.createElement("div", {
    className: cn(styles.qualifierContainer, {
      [styles.start]: !qualifierEnd,
      [styles.end]: qualifierEnd,
      [styles.textContainer]: isString(qualifier),
      [styles.stretch]: stretch
    })
  }, input(), /*#__PURE__*/React.createElement(QualifierWrap, {
    size: svgSize(size)
  }, qualifier)) : /*#__PURE__*/React.createElement(React.Fragment, null, input());
});
Textfield.displayName = "Textfield";
Textfield.propTypes = {
  /**
   * Controls size of the field. Accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Field matches the width of its container
   */
  stretch: PropTypes.bool,

  /**
   * Enables the field to accept multiple lines of text.
   *
   * `Qualifiers won't work with multiline`
   */
  multiline: PropTypes.bool,

  /**
   * Renders an icon or text before or after the field.
   * Accepts SVG or string.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Renders qualifier before the field
   */
  qualifierStart: PropTypes.bool,

  /**
   * Renders qualifier after the field
   */
  qualifierEnd: PropTypes.bool,

  /**
   * Controls the height of a `multiline` field.
   *
   * Accepts "low", "mid" & "high". "low" is the default.
   */
  extent: PropTypes.oneOf(["low", "mid", "high"]),

  /**
   * Adds error styles to the field if true.
   */
  invalid: PropTypes.bool
};
Textfield.defaultProps = {
  size: "m",
  stretch: false,
  multiline: false,
  qualifier: null,
  qualifierStart: true,
  qualifierEnd: false,
  extent: "low",
  invalid: false
};

const Email = /*#__PURE__*/forwardRef(({
  size,
  stretch,
  qualifier,
  qualifierStart,
  qualifierEnd,
  invalid,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    ...fieldProps
  } = props;

  const input = () => {
    return /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: "email",
      autoComplete: "email",
      className: cn(createFieldClasses({
        size,
        stretch,
        invalid
      })),
      "aria-invalid": invalid
    }));
  };

  return qualifier ? /*#__PURE__*/React.createElement("div", {
    className: cn(styles.qualifierContainer, {
      [styles.start]: !qualifierEnd,
      [styles.end]: qualifierEnd,
      [styles.stretch]: stretch,
      [styles.textContainer]: isString(qualifier)
    })
  }, input(), /*#__PURE__*/React.createElement(QualifierWrap, {
    size: svgSize(size)
  }, qualifier)) : /*#__PURE__*/React.createElement(React.Fragment, null, input());
});
Email.displayName = "Email";
Email.propTypes = {
  /**
   * Controls size of the field. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Field matches the width of its container
   */
  stretch: PropTypes.bool,

  /**
   * Renders an icon or text before or after the field.
   * Accepts SVG or string.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Renders qualifier before the field
   */
  qualifierStart: PropTypes.bool,

  /**
   * Renders qualifier after the field
   */
  qualifierEnd: PropTypes.bool,

  /**
   * Adds error styles to the field if true
   */
  invalid: PropTypes.bool
};
Email.defaultProps = {
  size: "m",
  stretch: false,
  qualifier: null,
  qualifierStart: true,
  qualifierEnd: false,
  invalid: false
};

const Password = /*#__PURE__*/forwardRef(({
  size,
  stretch,
  qualifier,
  qualifierStart,
  qualifierEnd,
  invalid,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    ...fieldProps
  } = props;

  const input = () => {
    return /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: "password",
      className: cn(createFieldClasses({
        size,
        stretch,
        invalid
      })),
      "aria-invalid": invalid
    }));
  };

  return qualifier ? /*#__PURE__*/React.createElement("div", {
    className: cn(styles.qualifierContainer, {
      [styles.start]: !qualifierEnd,
      [styles.end]: qualifierEnd,
      [styles.stretch]: stretch,
      [styles.textContainer]: isString(qualifier)
    })
  }, input(), /*#__PURE__*/React.createElement(QualifierWrap, {
    size: svgSize(size)
  }, qualifier)) : /*#__PURE__*/React.createElement(React.Fragment, null, input());
});
Password.displayName = "Password";
Password.propTypes = {
  /**
   * Controls size of the field. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Field matches the width of its container
   */
  stretch: PropTypes.bool,

  /**
   * Renders an icon or text before or after the field.
   * Accepts SVG or string.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Renders qualifier before the field
   */
  qualifierStart: PropTypes.bool,

  /**
   * Renders qualifier after the field
   */
  qualifierEnd: PropTypes.bool,

  /**
   * Adds error styles to the field if true
   */
  invalid: PropTypes.bool
};
Password.defaultProps = {
  size: "m",
  stretch: false,
  qualifier: null,
  qualifierStart: true,
  qualifierEnd: false,
  invalid: false
};

const URL = /*#__PURE__*/forwardRef(({
  size,
  stretch,
  qualifier,
  qualifierStart,
  qualifierEnd,
  invalid,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    ...fieldProps
  } = props;

  const input = () => {
    return /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: "url",
      className: cn(createFieldClasses({
        size,
        stretch,
        invalid
      })),
      "aria-invalid": invalid
    }));
  };

  return qualifier ? /*#__PURE__*/React.createElement("div", {
    className: cn(styles.qualifierContainer, {
      [styles.stretch]: stretch,
      [styles.start]: !qualifierEnd,
      [styles.end]: qualifierEnd,
      [styles.textContainer]: isString(qualifier)
    })
  }, input(), /*#__PURE__*/React.createElement(QualifierWrap, {
    size: svgSize(size)
  }, qualifier)) : /*#__PURE__*/React.createElement(React.Fragment, null, input());
});
URL.displayName = "URL";
URL.propTypes = {
  /**
   * Controls size of the field. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Field matches the width of its container
   */
  stretch: PropTypes.bool,

  /**
   * Renders an icon or text before or after the field.
   * Accepts SVG or text.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Renders qualifier before the field
   */
  qualifierStart: PropTypes.bool,

  /**
   * Renders qualifier after the field
   */
  qualifierEnd: PropTypes.bool,

  /**
   * Adds error styles to the field if true
   */
  invalid: PropTypes.bool
};
URL.defaultProps = {
  size: "m",
  stretch: false,
  qualifier: null,
  qualifierStart: true,
  qualifierEnd: false,
  invalid: false
};

const Numeric = /*#__PURE__*/forwardRef(({
  size,
  stretch,
  qualifier,
  qualifierStart,
  qualifierEnd,
  invalid,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    ...fieldProps
  } = props;

  const input = () => {
    return /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: "number",
      className: cn(createFieldClasses({
        size,
        stretch,
        invalid
      })),
      "aria-invalid": invalid
    }));
  };

  return qualifier ? /*#__PURE__*/React.createElement("div", {
    className: cn(styles.qualifierContainer, {
      [styles.start]: !qualifierEnd,
      [styles.end]: qualifierEnd,
      [styles.stretch]: stretch,
      [styles.textContainer]: isString(qualifier)
    })
  }, input(), /*#__PURE__*/React.createElement(QualifierWrap, {
    size: svgSize(size)
  }, qualifier)) : /*#__PURE__*/React.createElement(React.Fragment, null, input());
});
Numeric.displayName = "Numeric";
Numeric.propTypes = {
  /**
   * Controls size of the field. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Field matches the width of its container
   */
  stretch: PropTypes.bool,

  /**
   * Renders an icon or text before or after the field.
   * Accepts SVG or string.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Renders qualifier before the field
   */
  qualifierStart: PropTypes.bool,

  /**
   * Renders qualifier after the field
   */
  qualifierEnd: PropTypes.bool,

  /**
   * Adds error styles to the field if true
   */
  invalid: PropTypes.bool
};
Numeric.defaultProps = {
  size: "m",
  stretch: false,
  qualifier: null,
  qualifierStart: true,
  qualifierEnd: false,
  invalid: false
};

const SearchIcon = () => {
  const svgId = hashUtil.randomHash();
  return /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: svgId,
    d: "M15.55 13.432l-2.668-2.679a6.935 6.935 0 001.11-3.757A6.99 6.99 0 006.995 0 6.99 6.99 0 000 6.996a6.99 6.99 0 006.996 6.995c1.389 0 2.678-.42 3.757-1.11l2.679 2.67a1.497 1.497 0 102.119-2.119zm-8.554-1.44a4.999 4.999 0 01-4.997-4.996 4.999 4.999 0 014.997-4.997 4.999 4.999 0 014.997 4.997 4.999 4.999 0 01-4.997 4.997z"
  })), /*#__PURE__*/React.createElement("use", {
    fill: "#546584",
    fillRule: "evenodd",
    xlinkHref: `#${svgId}`
  }));
};

const Search = /*#__PURE__*/forwardRef(({
  size,
  stretch,
  invalid,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    ...fieldProps
  } = props;
  const searchFieldClasses = cn(styles.search, createFieldClasses({
    size,
    stretch,
    invalid
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles.qualifierContainer, styles.start, {
      [styles.stretch]: stretch
    })
  }, /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
    ref: ref,
    type: "search",
    className: searchFieldClasses,
    "aria-invalid": invalid
  })), /*#__PURE__*/React.createElement("span", {
    className: cn(styles.qualifier)
  }, /*#__PURE__*/React.createElement(Qualifier, {
    iconSize: svgSize(size)
  }, /*#__PURE__*/React.createElement(SearchIcon, null))));
});
Search.displayName = "Search";
Search.propTypes = {
  /**
   * Controls size of the field. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Field matches the width of its container
   */
  stretch: PropTypes.bool,

  /**
   * Adds error styles to the field if true
   */
  invalid: PropTypes.bool
};
Search.defaultProps = {
  size: "m",
  stretch: false,
  invalid: false
};

const Datefield = /*#__PURE__*/forwardRef(({
  size,
  stretch,
  qualifier,
  invalid,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out role, style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    role,
    ...fieldProps
  } = props;
  const {
    renderText,
    plugin
  } = useContext(context);

  if (plugin) {
    const showPlugin = Boolean(plugin) && renderText;
    return /*#__PURE__*/React.createElement("div", {
      className: cn(styles.qualifierContainer, {
        [styles.stretch]: stretch
      })
    }, /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: showPlugin ? "text" : "date",
      "aria-invalid": invalid,
      className: cn(createFieldClasses({
        size,
        stretch,
        invalid
      }), styles.datefield, {
        [styles.pluginEnd]: showPlugin
      })
    })), showPlugin && /*#__PURE__*/React.createElement("span", {
      className: cn(styles.qualifier, {
        [styles.pluginQualifier]: showPlugin
      })
    }, plugin));
  }
  /**
   * Need to repeat input fields, as, if we abstract it into a functional component,
   * the field looses focus on rerender
   */


  if (qualifier) {
    return /*#__PURE__*/React.createElement("div", {
      className: cn(styles.qualifierContainer, styles.start, {
        [styles.stretch]: stretch,
        [styles.textContainer]: isString(qualifier)
      })
    }, /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: "date",
      "aria-invalid": invalid,
      className: cn(createFieldClasses({
        size,
        stretch,
        invalid
      }), styles.datefield)
    })), /*#__PURE__*/React.createElement(QualifierWrap, {
      size: svgSize(size)
    }, qualifier));
  }

  return /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
    ref: ref,
    type: "date",
    "aria-invalid": invalid,
    className: cn(createFieldClasses({
      size,
      stretch,
      invalid
    }), styles.datefield)
  }));
});
Datefield.displayName = "Datefield";
Datefield.propTypes = {
  /**
   * Controls size of the field. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Field matches the width of its container
   */
  stretch: PropTypes.bool,

  /**
   * Renders an icon or text before or after the field.
   * Accepts SVG or string.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Adds error styles to the field if true
   */
  invalid: PropTypes.bool
};
Datefield.defaultProps = {
  size: "m",
  stretch: false,
  qualifier: null,
  invalid: false
};

const Timefield = /*#__PURE__*/forwardRef(({
  size,
  stretch,
  qualifier,
  invalid,
  ...props
}, ref) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    ...fieldProps
  } = props;
  const {
    renderText,
    plugin
  } = useContext(context);

  if (plugin) {
    const showPlugin = Boolean(plugin) && renderText;
    return /*#__PURE__*/React.createElement("div", {
      className: cn(styles.qualifierContainer, {
        [styles.stretch]: stretch
      })
    }, /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: showPlugin ? "text" : "time",
      "aria-invalid": invalid,
      className: cn(createFieldClasses({
        size,
        stretch,
        invalid
      }), styles.datefield, {
        [styles.pluginEnd]: showPlugin
      })
    })), showPlugin && /*#__PURE__*/React.createElement("span", {
      className: cn(styles.qualifier, {
        [styles.pluginQualifier]: showPlugin
      })
    }, plugin));
  }

  const input = () => {
    return /*#__PURE__*/React.createElement("input", _extends({}, fieldProps, {
      ref: ref,
      type: "time",
      "aria-invalid": invalid,
      className: cn(createFieldClasses({
        size,
        stretch,
        invalid
      }), styles.datefield)
    }));
  };

  if (qualifier) {
    return /*#__PURE__*/React.createElement("div", {
      className: cn(styles.qualifierContainer, styles.start, {
        [styles.stretch]: stretch,
        [styles.textContainer]: isString(qualifier)
      })
    }, input(), /*#__PURE__*/React.createElement(QualifierWrap, {
      size: svgSize(size)
    }, qualifier));
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, input());
});
Timefield.displayName = "Timefield";
Timefield.propTypes = {
  /**
   * Controls size of the field. Accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Field matches the width of its container
   */
  stretch: PropTypes.bool,

  /**
   * Renders an icon or text before or after the field.
   * Accepts SVG or string.
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /**
   * Adds error styles to the field if true
   */
  invalid: PropTypes.bool
};
Timefield.defaultProps = {
  size: "m",
  stretch: false,
  qualifier: null,
  invalid: false
};

export { Datefield, Email, Numeric, Password, Search, Timefield, URL, Textfield as default };
